import { ButtonLooks, FontIcons, IconButton, useFetch } from '@brandfolder/react';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';

import { ConfirmDeleteDialog } from '@components/dialogs';
import { BlockContext } from '@context/blocks';
import { BrandguideContext } from '@context/brandguide';
import { LocaleContext } from '@context/locale';
import { PageContext } from '@context/page';
import { PagesContext } from '@context/pages';
import { PublishContext } from '@context/publish';
import { BlockTypes } from '@enums/blocks';
import { ApiDatum } from '@typings/api';
import { BlockSection } from '@typings/block';
import { Page, PageGet, PagePatch } from '@typings/page';
import { createBlock, insertBlock } from '@utilities/block';
import { bulkPatchPagePositions, getIsCurrentPage, removePage, updatePage } from '@utilities/page';

import { PagesDragIcon } from './pages-drag-icon';
import { PagesEdit } from './pages-edit';
import { PagesName } from './pages-name';

interface PagesEditPageProps extends HTMLAttributes<HTMLDivElement> {
  page: Page;
}

const UnstyledPagesEditPage: FunctionComponent<PagesEditPageProps> = (props) => {
  const { page, ...otherProps } = props;

  const { blocks, sections, setBlocks } = useContext(BlockContext);
  const { brandguide } = useContext(BrandguideContext);
  const { locale } = useContext(LocaleContext);
  const { page: currentPage, pages, setPage, setPages } = useContext(PageContext);
  const { setEditingPage } = useContext(PagesContext);
  const { setPublish } = useContext(PublishContext);

  const [initialPage, setInitialPage] = useState(page);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const currentPageRef = useRef(currentPage);
  const initialPageRef = useRef(initialPage);
  const pageRef = useRef(page);
  const pagesRef = useRef(pages);

  const pageEndpointBase = `/api/pages/`;
  const current = getIsCurrentPage(currentPage as Page, page);
  const only = pages.length === 1;

  const pagePatch: Partial<PagePatch> = {
    name: page?.name || ''
  };

  const pagePatchFetch = useFetch<ApiDatum<PageGet>>({
    body: { data: { attributes: pagePatch } },
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `${pageEndpointBase}${page.key}`
  });

  const deletePageFetch = useFetch({
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    fetchOnMount: false,
    method: 'DELETE',
    url: `${pageEndpointBase}${page.key}`
  });

  const updatePageContext = (updatedPage: Partial<Page>): void => {
    if (page.key === currentPage?.key) {
      setPage({ ...page, ...updatedPage });
    }
    setPages([...updatePage({ ...page, ...updatedPage }, pages)]);
  };

  useEffect(() => {
    return () => {
      if (pageRef.current.name !== initialPageRef.current.name) {
        if (pageRef.current.key === currentPageRef.current?.key) {
          setPage({ ...pageRef.current, ...initialPageRef.current });
        }
        setPages([
          ...updatePage({ ...pageRef.current, ...initialPageRef.current }, pagesRef.current)
        ]);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    currentPageRef.current = currentPage;
    initialPageRef.current = initialPage;
    pageRef.current = page;
    pagesRef.current = pages;
  }, [currentPage, initialPage, page, pages]);

  useEffect(() => {
    if (pagePatchFetch.response) {
      updatePageContext(pagePatchFetch.response.data.attributes);
      setInitialPage({ ...initialPage, ...pagePatchFetch.response.data.attributes });
    }
  }, [pagePatchFetch.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (deletePageFetch.response) {
      const updatedPages = removePage(page, pages);
      setPages([...updatedPages]);

      bulkPatchPagePositions({
        pagesToUpdate: updatedPages.slice(page.position),
        pages: updatedPages,
        setPages,
        onComplete: () => setEditingPage(null),
        onError: () => setEditingPage(null)
      });

      setDeleteConfirmOpen(false);
      toast.success('Deleted!');
    }
  }, [deletePageFetch.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (deletePageFetch.error) {
      toast.error('Error saving.');
    }
  }, [deletePageFetch.error]);

  useEffect(() => {
    if (pagePatchFetch.error) {
      toast.error('Error saving.');
    }
  }, [pagePatchFetch.error]);

  const addSectionAndPublish = () => {
    const position = blocks.length === 0 ? 0 : blocks.length + 1;
    const sectionsLength = sections.length + 1;

    const name = `New Section Heading ${sectionsLength}`;

    const blockSection: BlockSection = {
      id: `new-section-heading-${sectionsLength}`,
      name
    };

    const sectionBlock = createBlock({
      data: blockSection,
      locale,
      pageKey: page.key,
      position,
      type: BlockTypes.Section
    });

    const textBlock = createBlock({
      data: {
        html: `<h2>${name}</h2>`
      },
      locale,
      pageKey: page.key,
      parentKey: sectionBlock.key,
      position: position + 1,
      type: BlockTypes.Text
    });

    const blocksWithSection = [...insertBlock(sectionBlock, blocks, page.key)];
    setBlocks([...insertBlock(textBlock, blocksWithSection, page.key)]);
    setPublish(true);
  };

  return (
    <PagesEdit {...otherProps} only={only} page>
      <PagesDragIcon only={only} />
      <PagesName current={current} name={page.name} page />
      <div className="pages-edit__buttons">
        <IconButton
          icon={FontIcons.Plus}
          label="Add section"
          look={ButtonLooks.Tertiary}
          onClick={() => addSectionAndPublish()}
        />
        <IconButton
          icon={FontIcons.Edit}
          label="Edit page"
          look={ButtonLooks.Tertiary}
          onClick={() => {
            setEditingPage(page);
          }}
        />
        <IconButton
          disabled={current}
          icon={FontIcons.Trash}
          label="Delete page"
          look={ButtonLooks.Tertiary}
          onClick={() => setDeleteConfirmOpen(true)}
        />
      </div>
      {!current && (
        <ConfirmDeleteDialog
          page={page}
          id={`confirm-delete-${page.key}`}
          loading={deletePageFetch.loading}
          onCancel={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            setEditingPage(null);
            deletePageFetch.fetch();
          }}
          open={deleteConfirmOpen}
          setOpen={setDeleteConfirmOpen}
        />
      )}
    </PagesEdit>
  );
};

const StyledPagesEditPage = styled(UnstyledPagesEditPage)`
  .pages-edit__buttons {
    flex-shrink: 0;
  }

  .bf-dialog__body {
    .confirm {
      margin-bottom: 32px;
    }

    .button-group {
      display: flex;
      justify-content: center;
    }

    .cancel {
      margin-right: 12px;
    }
  }
`;

export const PagesEditPage: FunctionComponent<PagesEditPageProps> = (props) => {
  return <StyledPagesEditPage {...props} />;
};
